import React, { useState, useEffect, useMemo } from 'react';
import Select2Dropdown from './Select2Dropdown';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { openFilePreview } from './FilePreviewModal';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';

const API_BASE = '/api/documentation/jobs';
const STAFF_API = '/api/admin/staff/?active=true';
const LOCATIONS_API = '/api/masters/drop-off-locations/';

const DO_STATUS_OPTIONS = [
  'DO RECEIVED, AWAITING DROP-OFF',
  'WAITING CLIENT CONFIRMATION',
  'WAITING CLIENT PAYMENT',
  'WAITING VESSEL ARRIVAL',
  'WAITING RENEWED DO',
  'WAITING MASTER DO',
  'WAITING DEM CONF',
  'WAITING INVOICE',
  'TO COLLECT DO',
  'WAITING DUTY',
  'WAITING PRV',
  'TO LODGE DO',
  'WAITING DO',
  'DO ON HOLD',
  'DO RECEIVED',
  'TO PAY'
];

const BL_TYPE_OPTIONS = ['NO OBL', 'SWB', 'TLX', 'OBL'];
const DO_RECEIVED = 'DO RECEIVED';
const DO_RECEIVED_AWAITING_DROPOFF = 'DO RECEIVED, AWAITING DROP-OFF';
const PRIMARY_LETTER_TYPES = new Set(['AUTHORIZATION', 'DO ATTACHMENT', 'DROPOFF ATTACHMENT']);

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
};

const normalizeDateInput = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr !== 'string') return dateStr;
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const ukMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ukMatch) return `${ukMatch[3]}-${ukMatch[2]}-${ukMatch[1]}`;
  return dateStr;
};

const fmtDateShort = d => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return d;
  }
};

const enforceDoReceivedRule = list => {
  if (!Array.isArray(list)) return [];
  if (list.includes(DO_RECEIVED)) return [DO_RECEIVED];
  if (list.includes(DO_RECEIVED_AWAITING_DROPOFF)) return [DO_RECEIVED_AWAITING_DROPOFF];
  return list;
};

const findLetterByType = (job, type) => {
  if (!job) return null;
  const letters = job.letters || job.job_letters || [];
  const upperType = type.toUpperCase();
  const matched = letters
    .filter(l => (l.letter_type || l.type || '').toUpperCase() === upperType)
    .sort((a, b) => {
      const aTime = new Date(a.created_at || a.date || 0).getTime() || 0;
      const bTime = new Date(b.created_at || b.date || 0).getTime() || 0;
      if (bTime !== aTime) return bTime - aTime;
      return (b.id || 0) - (a.id || 0);
    });
  return matched[0] || null;
};

const authLetterFor = job => findLetterByType(job, 'AUTHORIZATION');

const findLatestAdditionalLetter = job => {
  if (!job) return null;
  const letters = job.letters || job.job_letters || [];
  const matched = letters
    .filter(l => {
      const type = (l.letter_type || l.type || '').toString().trim().toUpperCase();
      return type && !PRIMARY_LETTER_TYPES.has(type);
    })
    .sort((a, b) => {
      const aTime = new Date(a.created_at || a.date || 0).getTime() || 0;
      const bTime = new Date(b.created_at || b.date || 0).getTime() || 0;
      if (bTime !== aTime) return bTime - aTime;
      return (b.id || 0) - (a.id || 0);
    });
  return matched[0] || null;
};

const initialNewDoEntry = () => ({
  isOpen: false,
  doReceivedDate: '',
  doExpiryDate: '',
  dropOffDate: '',
  dropOffExpiryDate: '',
  dropOffLocation: '',
  doAttachmentFile: null,
  doAttachmentPreviewUrl: '',
  dropOffAttachmentFile: null,
  dropoffAttachmentPreviewUrl: '',
  usePreviousDO: false,
  usePreviousDropoff: false,
  showDropoffExpiry: false,
});

/**
 * Shipping Details dialog — mirrors the BL-link dialog in ShippingReport.
 * Self-contained: fetches the job detail + DO records on open, and the
 * shipping staff / drop-off location lookups.
 *
 * Props:
 *   open     — boolean
 *   row      — report row (must contain the job `id`; optional sl_user_id/sl_user used as fallback)
 *   onClose  — close handler
 *   onSaved  — called after any successful save so the parent can refresh
 *   canEdit  — SHIPPING REPORTS can_edit
 *   canDelete— SHIPPING REPORTS can_delete
 */
const ShippingDetailsDialog = ({ open, row, onClose, onSaved, canEdit, canDelete }) => {
  const [dialog, setDialog] = useState(null); // mirrors shippingDetailsDialog state in ShippingReport
  const [shippingStaff, setShippingStaff] = useState([]);
  const [dropOffLocations, setDropOffLocations] = useState([]);

  const [showDropoffExpiryModal, setShowDropoffExpiryModal] = useState(false);
  const [dropoffExpiryContainers, setDropoffExpiryContainers] = useState([]);
  const [selectedDropoffContainerIds, setSelectedDropoffContainerIds] = useState([]);
  const [bulkDropoffExpiryDate, setBulkDropoffExpiryDate] = useState('');
  const [savingDropoffExpiry, setSavingDropoffExpiry] = useState(false);
  const [dropoffExpirySaveMode, setDropoffExpirySaveMode] = useState('staged');
  const [dropoffExpiryTarget, setDropoffExpiryTarget] = useState('dialog');

  const [editingDoRecord, setEditingDoRecord] = useState(null);
  const [doRecordEditDraft, setDoRecordEditDraft] = useState({});
  const [doRecordSaving, setDoRecordSaving] = useState(false);
  const [deletingDoRecord, setDeletingDoRecord] = useState(null);
  const [blLoading, setBlLoading] = useState(false);
  const [showFreeDaysConfirm, setShowFreeDaysConfirm] = useState(false);

  const canEditDropoffExpiryModal = canEdit || (dropoffExpirySaveMode === 'direct' && canDelete);
  const authHeaders = (json = true) => getAuthHeaders(!json);
  const notifySaved = () => { if (onSaved) onSaved(); };

  // Load job detail + DO records when the dialog opens
  useEffect(() => {
    if (!open || !row) return;
    let cancelled = false;
    const load = async () => {
      let job = row;
      try {
        const [detailRes, recordsRes] = await Promise.all([
          fetch(`${API_BASE}/${row.id}/`, { headers: authHeaders(false) }),
          fetch(`${API_BASE}/${row.id}/do-records/`, { headers: authHeaders(false) }),
        ]);
        if (detailRes.ok) {
          const detail = await detailRes.json();
          job = {
            ...detail,
            sl_user_id: detail.sl_user_id || row.sl_user_id || '',
            sl_user: detail.sl_user || row.sl_user || ''
          };
        }
        if (recordsRes.ok) {
          const records = await recordsRes.json();
          job = { ...job, do_records: Array.isArray(records) ? records : (job.do_records || []) };
        }
      } catch (e) {
        console.error('Failed to fetch full job details', e);
      }
      if (cancelled) return;

      const origDoDate = job.do_received_date || null;
      const origDoExpiry = job.do_expiry_date || null;
      const origDropDate = job.drop_off_date || null;
      const origDropExpiry = job.drop_off_expiry_date || null;
      const origDropLocation = job.drop_off_location || null;

      setDialog({
        open: true,
        job,
        origDoDate,
        origDoExpiry,
        origDropDate,
        origDropExpiry,
        origDropLocation,
        tempDOStatuses: enforceDoReceivedRule([...(job.do_statuses || [])]),
        tempBLType: job.bl_type || job.blType || 'NO OBL',
        tempSLUserId: job.sl_user_id || '',
        tempFreeDays: job.shipping_free_days ?? job.shipping_line_details?.free_days ?? 14,
        tempConfirmFreeDays: !!job.confirm_free_days,
        letterFile: null,
        letterAttachmentPreviewUrl: '',
        doReceivedDate: formatDateForInput(job.do_received_date),
        doExpiryDate: formatDateForInput(job.do_expiry_date),
        dropOffExpiryDate: formatDateForInput(job.drop_off_expiry_date),
        dropOffDate: formatDateForInput(job.drop_off_date),
        dropOffLocation: job.drop_off_location || '',
        doAttachmentFile: null,
        doAttachmentPreviewUrl: '',
        dropOffAttachmentFile: null,
        dropoffAttachmentPreviewUrl: '',
        additionalLetterType: '',
        additionalLetterFile: null,
        newDoEntry: initialNewDoEntry(),
        saving: false,
      });
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  // Load lookups once
  useEffect(() => {
    if (!open) return;
    fetch(STAFF_API, { headers: authHeaders(false) })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setShippingStaff((Array.isArray(data) ? data : []).filter(s =>
        (s.department_name || '').toUpperCase().includes('SHIPPING') && s.user_id != null)))
      .catch(() => {});
    fetch(LOCATIONS_API, { headers: authHeaders(false) })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setDropOffLocations(Array.isArray(data) ? data : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fallback: when neither the job detail nor the row carried sl_user_id,
  // resolve it from the shipping staff list by the stored sl_user name.
  useEffect(() => {
    if (!dialog || dialog.tempSLUserId || !(dialog.job?.sl_user || '').trim() || shippingStaff.length === 0) return;
    const target = (dialog.job.sl_user || '').trim().toUpperCase();
    const match = shippingStaff.find(s => (s.name || '').trim().toUpperCase() === target);
    if (match) setDialog(d => (d ? { ...d, tempSLUserId: match.id } : d));
  }, [shippingStaff, dialog]);

  const closeDialog = () => {
    if (dialog) {
      ['doAttachmentPreviewUrl', 'dropoffAttachmentPreviewUrl', 'letterAttachmentPreviewUrl'].forEach(k => {
        if (dialog[k]) URL.revokeObjectURL(dialog[k]);
      });
      if (dialog.newDoEntry?.doAttachmentPreviewUrl) URL.revokeObjectURL(dialog.newDoEntry.doAttachmentPreviewUrl);
      if (dialog.newDoEntry?.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(dialog.newDoEntry.dropoffAttachmentPreviewUrl);
    }
    setDialog(null);
    setEditingDoRecord(null);
    setDoRecordEditDraft({});
    setDeletingDoRecord(null);
    setShowDropoffExpiryModal(false);
    if (onClose) onClose();
  };

  const computeEarliestDropoffExpiry = (containers) => {
    const dates = (containers || []).map(c => normalizeDateInput(c.drop_off_expiry_date)).filter(Boolean);
    if (!dates.length) return null;
    return [...dates].sort()[0];
  };

  const computeLatestDropoffExpiry = (containers) => {
    const dates = (containers || []).map(c => normalizeDateInput(c.drop_off_expiry_date)).filter(Boolean);
    if (!dates.length) return null;
    const sorted = [...dates].sort();
    return sorted[sorted.length - 1];
  };

  const getDropoffExpiryContainerTitle = (containers) => (containers || [])
    .filter(c => c.container_no)
    .map(c => `${c.container_no} - ${c.drop_off_expiry_date ? fmtDateShort(c.drop_off_expiry_date) : 'Enter Date'}`)
    .join('\n');

  const hasMultipleDropoffExpiries = (containers) => new Set(
    (containers || []).filter(c => c.container_no).map(c => c.drop_off_expiry_date).filter(Boolean)
  ).size > 1;

  const dialogDoStatusOptions = useMemo(() => {
    const dialogCargoType = (dialog?.job?.cargo_type || dialog?.job?.shipment_type || '').toString().toUpperCase();
    const isLcl = dialogCargoType.includes('LCL') || dialogCargoType.includes('VEHICLE');
    if (dialog?.tempBLType === 'NO OBL') {
      return ['TO PAY', 'WAITING INVOICE', 'WAITING VESSEL ARRIVAL', 'WAITING CLIENT PAYMENT', 'INVOICE RECEIVED', 'PAID, WAITING BL'];
    }
    if (isLcl) {
      return DO_STATUS_OPTIONS.filter(status => status !== DO_RECEIVED_AWAITING_DROPOFF);
    }
    return DO_STATUS_OPTIONS;
  }, [dialog?.tempBLType, dialog?.job]);

  const hasDoDetailStatusSelected =
    (dialog?.tempDOStatuses ?? []).includes(DO_RECEIVED) ||
    (dialog?.tempDOStatuses ?? []).includes(DO_RECEIVED_AWAITING_DROPOFF);
  const isLclCargo = ((dialog?.job?.cargo_type || dialog?.job?.shipment_type || '').toString().toUpperCase().includes('LCL')) ||
    ((dialog?.job?.cargo_type || dialog?.job?.shipment_type || '').toString().toUpperCase().includes('VEHICLE'));
  const showDropoffFields = !isLclCargo && !(dialog?.tempDOStatuses ?? []).includes(DO_RECEIVED_AWAITING_DROPOFF);

  const handleDoStatusChange = (newStatuses) => {
    const { job, tempBLType, tempSLUserId, letterFile } = dialog;
    const lcl = ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('LCL')) ||
      ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('VEHICLE'));
    const hadAwaitingDropoffAlready = Array.isArray(job?.do_statuses) && job.do_statuses.includes(DO_RECEIVED_AWAITING_DROPOFF);
    let normalizedStatuses = newStatuses.map(v => v.toUpperCase());

    if (lcl && normalizedStatuses.includes(DO_RECEIVED_AWAITING_DROPOFF) && !hadAwaitingDropoffAlready) {
      toast.warn('DO RECEIVED, AWAITING DROP-OFF is not allowed for LCL/VEHICLE cargo.');
      return;
    }
    if (lcl && hadAwaitingDropoffAlready && normalizedStatuses.length > 1) {
      normalizedStatuses = normalizedStatuses.filter(status => status !== DO_RECEIVED_AWAITING_DROPOFF);
    }

    const isChangingToDoReceived = normalizedStatuses.includes(DO_RECEIVED);
    const isChangingToAwaitingDropoff = normalizedStatuses.includes(DO_RECEIVED_AWAITING_DROPOFF);

    if (isChangingToDoReceived || isChangingToAwaitingDropoff) {
      const hasAuthLetter = !!authLetterFor(job) || !!letterFile;
      if (!hasAuthLetter) {
        toast.warn('Please ensure an Authorization Letter is uploaded before setting status to DO RECEIVED.');
        return;
      }
      if (!tempBLType) {
        toast.warn('Please select a BL Type before setting status to DO RECEIVED.');
        return;
      }
      if (!tempSLUserId) {
        toast.warn('Please assign an SL User before setting status to DO RECEIVED.');
        return;
      }
    }

    setDialog(d => ({ ...d, tempDOStatuses: enforceDoReceivedRule(normalizedStatuses) }));
  };

  const openDropoffExpiryModal = (saveMode = 'staged', target = 'dialog') => {
    const job = dialog.job;
    const containers = (job?.containers || []).filter(c => c.container_no);
    setDropoffExpiryContainers(containers.map(c => ({ ...c, drop_off_expiry_date: normalizeDateInput(c.drop_off_expiry_date || '') })));
    setSelectedDropoffContainerIds([]);
    setBulkDropoffExpiryDate('');
    setDropoffExpirySaveMode(saveMode);
    setDropoffExpiryTarget(target);
    setShowDropoffExpiryModal(true);
  };

  const closeDropoffExpiryModal = () => {
    setShowDropoffExpiryModal(false);
    setDropoffExpiryContainers([]);
    setSelectedDropoffContainerIds([]);
    setBulkDropoffExpiryDate('');
    setDropoffExpirySaveMode('staged');
  };

  const handleDropoffExpirySelectionChange = (id) => {
    setSelectedDropoffContainerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleDropoffExpiryContainerChange = (id, value) => {
    setDropoffExpiryContainers(prev => prev.map(c => c.id === id ? { ...c, drop_off_expiry_date: value } : c));
  };

  const applyBulkDropoffExpiry = () => {
    if (!bulkDropoffExpiryDate || selectedDropoffContainerIds.length === 0) return;
    setDropoffExpiryContainers(prev => prev.map(c => selectedDropoffContainerIds.includes(c.id) ? { ...c, drop_off_expiry_date: bulkDropoffExpiryDate } : c));
  };

  const saveDropoffExpiryChanges = async () => {
    if (dropoffExpiryContainers.length === 0) return;
    const earliest = computeEarliestDropoffExpiry(dropoffExpiryContainers);

    if (dropoffExpirySaveMode === 'staged') {
      if (dropoffExpiryTarget === 'newdo') {
        setDialog(d => ({
          ...d,
          job: { ...d.job, containers: dropoffExpiryContainers },
          newDoEntry: { ...d.newDoEntry, dropOffExpiryDate: earliest || '', showDropoffExpiry: true },
        }));
      } else {
        setDialog(d => ({
          ...d,
          job: { ...d.job, containers: dropoffExpiryContainers },
          dropOffExpiryDate: earliest || '',
        }));
      }
      closeDropoffExpiryModal();
      return;
    }

    setSavingDropoffExpiry(true);
    try {
      const job = dialog.job;
      const containerRes = await fetch(`${API_BASE}/bulk-update-containers/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dropoffExpiryContainers.map(c => ({ id: c.id, drop_off_expiry_date: c.drop_off_expiry_date || null })))
      });
      if (!containerRes.ok) throw new Error('Failed to update container drop-off expiry dates.');

      const jobRes = await fetch(`${API_BASE}/${job.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ drop_off_expiry_date: earliest })
      });
      if (!jobRes.ok) throw new Error('Failed to update the drop-off expiry date.');
      const updatedJob = await jobRes.json();
      setDialog(d => ({
        ...d,
        job: { ...updatedJob, containers: dropoffExpiryContainers },
        dropOffExpiryDate: earliest || '',
      }));
      toast.success('Container drop-off expiry dates saved.');
      closeDropoffExpiryModal();
      notifySaved();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to save drop-off expiry dates.');
    } finally {
      setSavingDropoffExpiry(false);
    }
  };

  // Fetch + preview the BL document on demand (never loaded with the page)
  const viewBlDocument = async () => {
    const job = dialog?.job;
    if (!job || blLoading) return;
    setBlLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${job.id}/bl-document/`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error('not found');
      const data = await res.json();
      if (!data.url) throw new Error('not found');
      const fileRes = await fetch(data.url, { headers: authHeaders(false) });
      if (!fileRes.ok) throw new Error('not found');
      const blob = await fileRes.blob();
      const blobUrl = URL.createObjectURL(blob);
      const fileName = data.name || 'BL Document';
      const opened = openFilePreview(blobUrl, /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}.pdf`);
      if (!opened) {
        URL.revokeObjectURL(blobUrl);
        throw new Error('not found');
      }
    } catch (e) {
      toast.error('The BL document is not available for this job.');
    } finally {
      setBlLoading(false);
    }
  };

  const handleConfirmFreeDaysChange = (checked) => {
    if (checked) {
      setShowFreeDaysConfirm(true);
    } else {
      setDialog(d => ({ ...d, tempConfirmFreeDays: false }));
    }
  };

  const saveShippingDetails = async () => {
    const {
      job, tempBLType, tempDOStatuses, tempSLUserId, letterFile,
      doReceivedDate, doExpiryDate, dropOffExpiryDate, dropOffDate, dropOffLocation,
      doAttachmentFile, dropOffAttachmentFile, additionalLetterType, additionalLetterFile,
      newDoEntry, tempFreeDays, tempConfirmFreeDays
    } = dialog;
    if (!job) return;
    const normalizedAdditionalLetterType = (additionalLetterType || '').trim();

    const lcl = ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('LCL')) ||
      ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('VEHICLE'));
    const hasDoReceivedStatus = tempDOStatuses.includes(DO_RECEIVED);
    const hasAwaitingDropoffStatus = tempDOStatuses.includes(DO_RECEIVED_AWAITING_DROPOFF);
    const shouldShowDropoffFields = hasDoReceivedStatus && !hasAwaitingDropoffStatus && !lcl;

    const containers = job?.containers || [];
    const singleContainer = containers.length === 1 ? containers[0] : null;
    const isMultiContainer = containers.length > 1;
    const containerDropOffExpiry = computeEarliestDropoffExpiry(containers) || '';
    const effectiveDropOffExpiry = isMultiContainer
      ? (containerDropOffExpiry || dropOffExpiryDate || '')
      : dropOffExpiryDate;
    const maxDropOffExpiry = isMultiContainer
      ? (computeLatestDropoffExpiry(containers) || dropOffExpiryDate || '')
      : dropOffExpiryDate;
    const effectiveNewDropOffExpiry = newDoEntry.dropOffExpiryDate || '';

    const existingDoRecords = Array.isArray(job.do_records) ? job.do_records : [];
    const { origDoDate } = dialog;
    const hasExistingDoRecord = existingDoRecords.length > 0;

    const newDo = newDoEntry || {};
    const isAddingNewDoEntry = newDo.isOpen || !!(newDo.doReceivedDate || newDo.doAttachmentFile || newDo.usePreviousDO);

    const today = new Date().toISOString().split('T')[0];

    if (hasDoReceivedStatus || hasAwaitingDropoffStatus) {
      if (isAddingNewDoEntry) {
        if (!newDo.usePreviousDO) {
          if (!newDo.doReceivedDate) {
            toast.error('Please provide DO Received Date for the new entry.');
            return;
          }
          if (newDo.doReceivedDate > today) {
            toast.error('DO Received Date cannot be a future date.');
            return;
          }
          if (newDo.doExpiryDate && newDo.doExpiryDate < newDo.doReceivedDate) {
            toast.error('DO Expiry Date cannot be before DO Received Date.');
            return;
          }
        }
        if (shouldShowDropoffFields && !newDo.usePreviousDropoff) {
          if (!newDo.dropOffDate) {
            toast.error('Please provide Drop-Off Received Date for the new entry.');
            return;
          }
          const newDoDropOffDate = normalizeDateInput(newDo.dropOffDate);
          if (newDoDropOffDate > today) {
            toast.error('Drop-Off Received Date cannot be a future date.');
            return;
          }
          const hasNewDropOffExpiry = isMultiContainer
            ? (containers.length > 0 && containers.every(c => normalizeDateInput(c.drop_off_expiry_date)))
            : !!normalizeDateInput(newDo.dropOffExpiryDate);
          if (!hasNewDropOffExpiry) {
            toast.error(isMultiContainer ? 'Drop-Off Expiry Date is required for all containers.' : 'Please provide Drop-Off Expiry Date for the new entry.');
            return;
          }
          const maxNewDropOffExpiry = isMultiContainer
            ? (computeLatestDropoffExpiry(containers) || '')
            : (normalizeDateInput(newDo.dropOffExpiryDate) || '');
          if (maxNewDropOffExpiry && maxNewDropOffExpiry < newDoDropOffDate) {
            toast.error('Drop-Off Expiry Date cannot be before Drop-Off Received Date.');
            return;
          }
          if (newDo.dropOffDate && !newDo.dropOffAttachmentFile) {
            toast.error('Please attach Drop-Off Attachment for the new entry.');
            return;
          }
        }
      } else if (!hasExistingDoRecord) {
        if (!doReceivedDate) {
          toast.error('Please provide DO Received Date.');
          return;
        }
        if (doReceivedDate > today) {
          toast.error('DO Received Date cannot be a future date.');
          return;
        }
        if (doExpiryDate && doExpiryDate < doReceivedDate) {
          toast.error('DO Expiry Date cannot be before DO Received Date.');
          return;
        }
        if (shouldShowDropoffFields) {
          if (!dropOffDate) {
            toast.error('Please provide Drop-Off Received Date.');
            return;
          }
          if (dropOffDate > today) {
            toast.error('Drop-Off Received Date cannot be a future date.');
            return;
          }
          if (maxDropOffExpiry && maxDropOffExpiry < normalizeDateInput(dropOffDate)) {
            toast.error('Drop-Off Expiry Date cannot be before Drop-Off Received Date.');
            return;
          }
          if (dropOffDate && effectiveDropOffExpiry && !dropOffAttachmentFile) {
            toast.error(`Please attach Drop-Off Attachment. (dropOffDate: ${dropOffDate || 'empty'}, expiry: ${effectiveDropOffExpiry || 'empty'})`);
            return;
          }
        }
      }
    }

    if (!canEdit) {
      toast.error("You do not have permission to save changes.");
      return;
    }

    if (!tempConfirmFreeDays) {
      toast.error("Please select 'Confirm Free Days' before saving.");
      return;
    }

    setDialog(d => ({ ...d, saving: true }));

    try {
      const promises = [];
      const patchData = {};

      if (tempBLType && tempBLType !== (job.bl_type || job.blType)) {
        patchData.bl_type = tempBLType;
      }
      const cleanedDOStatuses = enforceDoReceivedRule(tempDOStatuses);
      if (JSON.stringify(cleanedDOStatuses) !== JSON.stringify(job.do_statuses)) {
        patchData.do_statuses = cleanedDOStatuses;
      }
      if (tempSLUserId) {
        const staff = shippingStaff.find(s => String(s.id) === String(tempSLUserId));
        const newName = staff ? staff.name : '';
        if (newName && newName !== job.sl_user) {
          patchData.sl_user = newName;
        }
      }
      const parsedFreeDays = tempFreeDays === '' || tempFreeDays === null ? null : parseInt(tempFreeDays, 10);
      if (parsedFreeDays !== (job.shipping_free_days ?? null)) {
        patchData.shipping_free_days = parsedFreeDays;
      }
      if (!!tempConfirmFreeDays !== !!job.confirm_free_days) {
        patchData.confirm_free_days = !!tempConfirmFreeDays;
      }

      if (!hasExistingDoRecord && !isAddingNewDoEntry && (doReceivedDate || doExpiryDate || (shouldShowDropoffFields && (dropOffDate || effectiveDropOffExpiry || dropOffLocation)))) {
        const doFd = new FormData();
        doFd.append('skip_auto_migrate', 'true');
        if (doReceivedDate) doFd.append('do_received_date', doReceivedDate);
        if (doExpiryDate) doFd.append('do_expiry_date', doExpiryDate);
        if (doAttachmentFile) doFd.append('do_attachment', doAttachmentFile);
        if (shouldShowDropoffFields) {
          if (dropOffDate) doFd.append('drop_off_date', dropOffDate);
          if (effectiveDropOffExpiry) doFd.append('drop_off_expiry_date', effectiveDropOffExpiry);
          if (dropOffLocation) doFd.append('drop_off_location', dropOffLocation);
          if (dropOffAttachmentFile) doFd.append('drop_off_attachment', dropOffAttachmentFile);
        }
        promises.push(
          fetch(`${API_BASE}/${job.id}/do-records/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: doFd,
          })
        );
      }

      if (Object.keys(patchData).length > 0) {
        promises.push(
          fetch(`${API_BASE}/${job.id}/`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify(patchData),
          })
        );
      }

      if (letterFile) {
        const fd = new FormData();
        fd.append('letter_type', 'AUTHORIZATION');
        fd.append('file', letterFile);
        promises.push(
          fetch(`${API_BASE}/${job.id}/letters/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: fd,
          })
        );
      }

      if (isAddingNewDoEntry) {
        const doFd = new FormData();
        if (newDo.usePreviousDO) {
          doFd.append('use_previous_do', 'true');
        } else {
          doFd.append('do_received_date', newDo.doReceivedDate);
          if (newDo.doExpiryDate) doFd.append('do_expiry_date', newDo.doExpiryDate);
          if (newDo.doAttachmentFile) doFd.append('do_attachment', newDo.doAttachmentFile);
        }
        if (shouldShowDropoffFields) {
          if (newDo.usePreviousDropoff) {
            doFd.append('use_previous_dropoff', 'true');
          } else {
            if (newDo.dropOffDate) doFd.append('drop_off_date', newDo.dropOffDate);
            if (effectiveNewDropOffExpiry) doFd.append('drop_off_expiry_date', effectiveNewDropOffExpiry);
            if (newDo.dropOffLocation) doFd.append('drop_off_location', newDo.dropOffLocation);
            if (newDo.dropOffAttachmentFile) doFd.append('drop_off_attachment', newDo.dropOffAttachmentFile);
          }
        }
        promises.push(
          fetch(`${API_BASE}/${job.id}/do-records/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: doFd,
          })
        );
      }

      if (shouldShowDropoffFields && containers.length > 0) {
        const effectiveDate = isAddingNewDoEntry ? effectiveNewDropOffExpiry : (hasExistingDoRecord ? '' : effectiveDropOffExpiry);
        const containerUpdates = isMultiContainer
          ? containers.map(c => ({ id: c.id, drop_off_expiry_date: c.drop_off_expiry_date || null }))
          : effectiveDate ? [{ id: singleContainer.id, drop_off_expiry_date: effectiveDate }] : [];
        if (containerUpdates.length > 0) {
          promises.push(
            fetch(`${API_BASE}/bulk-update-containers/`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify(containerUpdates)
            })
          );
        }
      }

      if (normalizedAdditionalLetterType && additionalLetterFile) {
        const fd = new FormData();
        fd.append('letter_type', normalizedAdditionalLetterType);
        fd.append('file', additionalLetterFile);
        promises.push(
          fetch(`${API_BASE}/${job.id}/letters/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: fd,
          })
        );
      }

      if (promises.length === 0) {
        closeDialog();
        return;
      }

      const responses = await Promise.all(promises);
      const allOk = responses.every(res => res.ok);

      if (allOk) {
        toast.success('Shipping details saved successfully!');
        closeDialog();
        notifySaved();
      } else {
        for (const res of responses) {
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to save details: ${errorText}`);
          }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'An error occurred while saving.');
    } finally {
      setDialog(d => (d ? { ...d, saving: false } : d));
    }
  };

  const startEditDoRecord = (rec) => {
    setEditingDoRecord(rec.id);
    setDoRecordEditDraft({
      do_received_date: formatDateForInput(rec.do_received_date),
      do_expiry_date: formatDateForInput(rec.do_expiry_date),
      drop_off_date: formatDateForInput(rec.drop_off_date),
      drop_off_expiry_date: formatDateForInput(rec.drop_off_expiry_date),
      drop_off_location: rec.drop_off_location || '',
      doAttachmentFile: null,
      dropOffAttachmentFile: null,
    });
  };

  const cancelEditDoRecord = () => {
    setEditingDoRecord(null);
    setDoRecordEditDraft({});
  };

  const updateDoRecordDraft = (field, value) => {
    setDoRecordEditDraft(d => ({ ...d, [field]: value }));
  };

  const updateDoRecordInState = (updated) => {
    setDialog(d => ({
      ...d,
      job: {
        ...d.job,
        do_records: (d.job?.do_records || []).map(r => r.id === updated.id ? updated : r)
      }
    }));
  };

  const updateOriginalDoInState = (updated) => {
    const newLetters = [];
    if (updated.do_attachment_url) {
      newLetters.push({ letter_type: 'DO ATTACHMENT', generated_file: updated.do_attachment_url, created_at: new Date().toISOString() });
    }
    if (updated.drop_off_attachment_url) {
      newLetters.push({ letter_type: 'DROPOFF ATTACHMENT', generated_file: updated.drop_off_attachment_url, created_at: new Date().toISOString() });
    }
    setDialog(d => ({
      ...d,
      origDoDate: updated.do_received_date ? formatDateForInput(updated.do_received_date) : d.origDoDate,
      origDoExpiry: updated.do_expiry_date ? formatDateForInput(updated.do_expiry_date) : d.origDoExpiry,
      origDropDate: updated.drop_off_date ? formatDateForInput(updated.drop_off_date) : d.origDropDate,
      origDropExpiry: updated.drop_off_expiry_date ? formatDateForInput(updated.drop_off_expiry_date) : d.origDropExpiry,
      origDropLocation: updated.drop_off_location !== undefined ? (updated.drop_off_location || '') : d.origDropLocation,
      job: {
        ...d.job,
        do_received_date: updated.do_received_date,
        do_expiry_date: updated.do_expiry_date,
        drop_off_date: updated.drop_off_date,
        drop_off_expiry_date: updated.drop_off_expiry_date,
        drop_off_location: updated.drop_off_location,
        letters: [...(d.job?.letters || []), ...newLetters],
      }
    }));
  };

  const removeDoRecordFromState = (recordId) => {
    setDialog(d => ({
      ...d,
      job: {
        ...d.job,
        do_records: (d.job?.do_records || []).filter(r => r.id !== recordId)
      }
    }));
  };

  const saveDoRecordEdit = async (rec) => {
    const draft = doRecordEditDraft;
    if (!draft) return;
    setDoRecordSaving(true);
    try {
      const job = dialog.job;
      const containers = (job?.containers || []).filter(c => c.container_no);
      const isMultiContainer = containers.length > 1;
      const effectiveDropOffExpiry = isMultiContainer
        ? (computeEarliestDropoffExpiry(containers) || '')
        : (normalizeDateInput(draft.drop_off_expiry_date) || '');
      const maxDropOffExpiry = isMultiContainer
        ? (computeLatestDropoffExpiry(containers) || '')
        : (normalizeDateInput(draft.drop_off_expiry_date) || '');

      if (draft.do_expiry_date && draft.do_received_date && draft.do_expiry_date < draft.do_received_date) {
        toast.error('DO Expiry Date cannot be before DO Received Date.');
        return;
      }
      if (showDropoffFields) {
        const dropOffReceived = normalizeDateInput(draft.drop_off_date);
        if (draft.do_received_date && !dropOffReceived) {
          toast.error('Drop-Off Received Date is required.');
          return;
        }
        if (isMultiContainer && draft.do_received_date) {
          const missingExpiries = containers.filter(c => !normalizeDateInput(c.drop_off_expiry_date));
          if (missingExpiries.length > 0) {
            toast.error(`Drop-Off Expiry Date is required for container(s): ${missingExpiries.map(c => c.container_no).join(', ')}.`);
            return;
          }
        }
        if (draft.do_received_date && !effectiveDropOffExpiry) {
          toast.error('Drop-Off Expiry Date is required.');
          return;
        }
        if (dropOffReceived && maxDropOffExpiry && maxDropOffExpiry < dropOffReceived) {
          toast.error('Drop-Off Expiry Date cannot be before Drop-Off Received Date.');
          return;
        }
        if (draft.do_received_date && !draft.dropOffAttachmentFile && !rec.drop_off_attachment_url) {
          toast.error('Drop-Off Attachment is required.');
          return;
        }
      }

      const isOriginal = rec.id === 'original' || rec._virtual;
      const fd = new FormData();
      if (isOriginal) {
        fd.append('job_id', job.id);
      } else {
        fd.append('record_id', rec.id);
      }
      if (!isMultiContainer && containers[0]?.id) {
        fd.append('container_id', containers[0].id);
      }
      fd.append('do_received_date', draft.do_received_date || '');
      fd.append('do_expiry_date', draft.do_expiry_date || '');
      if (showDropoffFields) {
        fd.append('drop_off_date', normalizeDateInput(draft.drop_off_date) || '');
        fd.append('drop_off_expiry_date', effectiveDropOffExpiry);
        fd.append('drop_off_location', draft.drop_off_location || '');
      }
      if (draft.doAttachmentFile) fd.append('do_attachment', draft.doAttachmentFile);
      if (draft.dropOffAttachmentFile) fd.append('drop_off_attachment', draft.dropOffAttachmentFile);

      const endpoint = isOriginal ? 'update-original-do-record' : 'update-do-record';
      const res = await fetch(`${API_BASE}/${endpoint}/`, {
        method: 'PATCH',
        headers: authHeaders(false),
        body: fd,
      });
      if (!res.ok) throw new Error('Failed to update DO record.');
      const data = await res.json();
      if (isOriginal) {
        updateOriginalDoInState(data);
      } else {
        updateDoRecordInState(data);
      }
      toast.success('DO record updated.');
      cancelEditDoRecord();
      notifySaved();
    } catch (e) {
      toast.error(e.message || 'Failed to update DO record.');
    } finally {
      setDoRecordSaving(false);
    }
  };

  const openDeleteDoRecordModal = (rec) => setDeletingDoRecord(rec.id);
  const closeDeleteDoRecordModal = () => setDeletingDoRecord(null);

  const doDeleteDoRecord = async () => {
    if (!deletingDoRecord) return;
    setDoRecordSaving(true);
    try {
      const res = await fetch(`${API_BASE}/delete-do-record/`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({ record_id: deletingDoRecord }),
      });
      if (!res.ok) throw new Error('Failed to delete DO record.');
      removeDoRecordFromState(deletingDoRecord);
      toast.success('DO record deleted.');
      notifySaved();
    } catch (e) {
      toast.error(e.message || 'Failed to delete DO record.');
    } finally {
      setDoRecordSaving(false);
      closeDeleteDoRecordModal();
    }
  };

  if (!open || !dialog) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="dialog-container" style={{ width: 650, maxHeight: '90vh', overflowY: 'auto', position: 'relative', margin: 'auto' }}>
          <div className="dialog-header">
            <h3>Shipping Details (IDF: {dialog.job?.idf_no || 'N/A'})</h3>
          </div>
          <div className="dialog-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Authorization Letter</label>
                {authLetterFor(dialog.job) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => {
                      const letter = authLetterFor(dialog.job);
                      const url = letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || '';
                      if (url) openFilePreview(url, 'Authorization Letter');
                    }} >View Existing Letter</button>
                    <input type="file" onChange={e => {
                      const file = e.target.files[0];
                      setDialog(d => {
                        if (d.letterAttachmentPreviewUrl) URL.revokeObjectURL(d.letterAttachmentPreviewUrl);
                        return { ...d, letterFile: file, letterAttachmentPreviewUrl: file ? URL.createObjectURL(file) : '' };
                      });
                    }} disabled={!canEdit} />
                    {dialog.letterAttachmentPreviewUrl && (
                      <button
                        type="button"
                        className="dialog-btn dialog-btn-primary dialog-btn-sm"
                        onClick={() => openFilePreview(dialog.letterAttachmentPreviewUrl, dialog.letterFile?.name || 'Selected Authorization Letter')}
                      >
                        View Selected Letter
                      </button>
                    )}
                  </div>
                ) : (<>
                  <input type="file" onChange={e => {
                    const file = e.target.files[0];
                    setDialog(d => {
                      if (d.letterAttachmentPreviewUrl) URL.revokeObjectURL(d.letterAttachmentPreviewUrl);
                      return { ...d, letterFile: file, letterAttachmentPreviewUrl: file ? URL.createObjectURL(file) : '' };
                    });
                  }} disabled={!canEdit} />
                  {dialog.letterAttachmentPreviewUrl && (
                    <button
                      type="button"
                      className="dialog-btn dialog-btn-primary dialog-btn-sm"
                      onClick={() => openFilePreview(dialog.letterAttachmentPreviewUrl, dialog.letterFile?.name || 'Selected Authorization Letter')}
                    >
                      View Selected Letter
                    </button>
                  )}
                </>)}
              </div>
              <div>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>BL Type</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <Select2Dropdown options={BL_TYPE_OPTIONS.map(o => ({ id: o, text: o }))} value={dialog.tempBLType} onChange={val => setDialog(d => ({ ...d, tempBLType: val }))} valueKey="id" displayKey="text" placeholder="Select BL Type" disabled={!canEdit} />
                  </div>
                  <a
                    href="#"
                    onClick={e => { e.preventDefault(); viewBlDocument(); }}
                    style={{ color: '#0d6efd', cursor: blLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap', fontSize: '0.85em' }}
                    title="View the BL document for this job"
                  >
                    {blLoading ? 'Loading…' : 'View BL'}
                  </a>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label htmlFor="shipping-free-days" style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Free Days</label>
                    <input
                      type="number"
                      id="shipping-free-days"
                      min="0"
                      max="99"
                      className="search-input"
                      value={dialog.tempFreeDays ?? ''}
                      onChange={e => setDialog(d => ({ ...d, tempFreeDays: e.target.value }))}
                      disabled={!(canDelete || (canEdit && !dialog.job?.confirm_free_days))}
                      style={{ width: 45, minWidth: 0, padding: '6px 2px', textAlign: 'center' }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: (canDelete || !dialog.job?.confirm_free_days) ? 'pointer' : 'not-allowed' }}>
                      <input
                        type="checkbox"
                        checked={!!dialog.tempConfirmFreeDays}
                        onChange={e => handleConfirmFreeDaysChange(e.target.checked)}
                        disabled={!canDelete && !!dialog.job?.confirm_free_days}
                      />
                      Confirm Free Days
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>SL User</label>
                <Select2Dropdown options={shippingStaff.map(s => ({ id: s.id, text: s.name }))} value={dialog.tempSLUserId} onChange={val => setDialog(d => ({ ...d, tempSLUserId: val }))} valueKey="id" displayKey="text" placeholder="Select SL User" disabled={!canEdit} />
              </div>
              <div>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Status</label>
                <MultiSelectDropdown id={`do-status-dialog-${dialog.job?.id}`} options={dialogDoStatusOptions} selectedValues={dialog.tempDOStatuses} onChange={handleDoStatusChange} placeholder="Select DO Status" disabled={!canEdit} />
              </div>

              {hasDoDetailStatusSelected && (
                <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 8, color: '#333' }}>DO Received Details</h4>

                  {/* ── History: existing read-only DO records ── */}
                  {(() => {
                    const job = dialog.job;
                    const dbRecords = Array.isArray(job?.do_records) ? job.do_records : [];

                    let displayRecords = [...dbRecords];
                    const { origDoDate, origDoExpiry, origDropDate, origDropExpiry, origDropLocation } = dialog;
                    if (dbRecords.length === 0 && origDoDate) {
                      const doLetter = findLetterByType(job, 'DO ATTACHMENT');
                      const dropLetter = findLetterByType(job, 'DROPOFF ATTACHMENT');
                      displayRecords = [{
                        id: 'original',
                        _virtual: true,
                        do_received_date: origDoDate,
                        do_expiry_date: origDoExpiry || null,
                        drop_off_date: origDropDate || null,
                        drop_off_expiry_date: origDropExpiry || null,
                        drop_off_location: origDropLocation || null,
                        do_attachment_url: doLetter ? (doLetter.generated_file || doLetter.file || doLetter.document || doLetter.url || doLetter.file_url || null) : null,
                        drop_off_attachment_url: dropLetter ? (dropLetter.generated_file || dropLetter.file || dropLetter.document || dropLetter.url || dropLetter.file_url || null) : null,
                        recorded_by_username: null,
                      }];
                    }

                    if (displayRecords.length === 0) return null;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ fontWeight: 'bold', fontSize: 12, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>
                          DO History ({displayRecords.length} {displayRecords.length === 1 ? 'entry' : 'entries'})
                        </div>
                        {displayRecords.map((rec, idx) => {
                          const isOriginal = rec._virtual || (idx === 0 && !rec.recorded_by_username);
                          return (
                            <div key={rec.id || idx} style={{ background: isOriginal ? '#f4f8ff' : '#f0f8f0', border: `1px solid ${isOriginal ? '#c5d8f5' : '#b2d8b2'}`, borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                <span style={{ fontWeight: 'bold', fontSize: 12, color: isOriginal ? '#555' : '#1a5fb4' }}>
                                  {isOriginal ? 'Original Entry' : `Entry #${idx + 1}`}
                                </span>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                  {rec.recorded_by_username && (
                                    <span style={{ fontSize: 11, color: '#888' }}>By: {rec.recorded_by_username}</span>
                                  )}
                                  {!isOriginal && rec.created_at && (
                                    <span style={{ fontSize: 11, color: '#888' }}>Saved: {fmtDateShort(rec.created_at)}</span>
                                  )}
                                  {rec.id && canDelete && editingDoRecord !== rec.id && (
                                    <>
                                      <button type="button" className="icon-btn" title="Edit" onClick={() => startEditDoRecord(rec)} style={{ color: '#1a5fb4' }}>
                                        <i className="fa fa-pencil" />
                                      </button>
                                      {!rec._virtual && (
                                        <button type="button" className="icon-btn" title="Delete" onClick={() => openDeleteDoRecordModal(rec)} style={{ color: '#c62828' }}>
                                          <i className="fa fa-trash" />
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {editingDoRecord === rec.id && (
                                    <>
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => saveDoRecordEdit(rec)} disabled={doRecordSaving}>
                                        {doRecordSaving ? 'Saving...' : 'Save'}
                                      </button>
                                      <button type="button" className="dialog-btn dialog-btn-secondary dialog-btn-sm" onClick={cancelEditDoRecord} disabled={doRecordSaving}>
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              {editingDoRecord === rec.id ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                                    <div>
                                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>DO Received Date</label>
                                      <input type="date" className="search-input" value={doRecordEditDraft.do_received_date || ''} onChange={e => updateDoRecordDraft('do_received_date', e.target.value)} />
                                    </div>
                                    <div>
                                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>DO Expiry Date</label>
                                      <input type="date" className="search-input" value={doRecordEditDraft.do_expiry_date || ''} onChange={e => updateDoRecordDraft('do_expiry_date', e.target.value)} min={doRecordEditDraft.do_received_date || undefined} />
                                    </div>
                                  </div>
                                  {showDropoffFields && (
                                    <>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                                        <div>
                                          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Received Date</label>
                                          <input type="date" className="search-input" value={doRecordEditDraft.drop_off_date || ''} onChange={e => updateDoRecordDraft('drop_off_date', e.target.value)} />
                                        </div>
                                        <div>
                                          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Expiry Date</label>
                                          {(dialog.job?.containers || []).filter(c => c.container_no).length > 1 ? (
                                            <a
                                              href="#"
                                              onClick={e => { e.preventDefault(); openDropoffExpiryModal(); }}
                                              title={getDropoffExpiryContainerTitle(dialog.job.containers)}
                                              style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                            >
                                              {fmtDateShort(computeEarliestDropoffExpiry(dialog.job.containers)) || 'Enter Date'}
                                            </a>
                                          ) : (
                                            <input type="date" className="search-input" value={doRecordEditDraft.drop_off_expiry_date || ''} onChange={e => updateDoRecordDraft('drop_off_expiry_date', e.target.value)} min={doRecordEditDraft.drop_off_date || undefined} />
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Location</label>
                                        <Select2Dropdown
                                          options={dropOffLocations.map(loc => ({ id: loc.name, text: loc.display_name }))}
                                          value={doRecordEditDraft.drop_off_location || ''}
                                          onChange={val => updateDoRecordDraft('drop_off_location', val)}
                                          valueKey="id"
                                          displayKey="text"
                                          placeholder="Select or type location"
                                          tags={true}
                                          disabled={!canEdit}
                                        />
                                      </div>
                                    </>
                                  )}
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>DO Attachment</label>
                                      {rec.do_attachment_url && (
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                                          <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.do_attachment_url, 'DO Attachment')}>View Current</button>
                                          <span style={{ fontSize: 11, color: '#888' }}>Replace:</span>
                                        </div>
                                      )}
                                      <input type="file" onChange={e => updateDoRecordDraft('doAttachmentFile', e.target.files[0])} />
                                    </div>
                                    {showDropoffFields && (
                                      <div style={{ flex: 1, minWidth: 200 }}>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Attachment</label>
                                        {rec.drop_off_attachment_url && (
                                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                                            <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.drop_off_attachment_url, 'Drop-Off Attachment')}>View Current</button>
                                            <span style={{ fontSize: 11, color: '#888' }}>Replace:</span>
                                          </div>
                                        )}
                                        <input type="file" onChange={e => updateDoRecordDraft('dropOffAttachmentFile', e.target.files[0])} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 12 }}>
                                    <div><span style={{ color: '#555' }}>DO Received:</span> <strong>{fmtDateShort(rec.do_received_date) || '—'}</strong></div>
                                    <div><span style={{ color: '#555' }}>DO Expiry:</span> <strong>{fmtDateShort(rec.do_expiry_date) || '—'}</strong></div>
                                    {showDropoffFields && (
                                      <>
                                        <div><span style={{ color: '#555' }}>Drop-Off Received:</span> <strong>{fmtDateShort(rec.drop_off_date) || '—'}</strong></div>
                                        <div>
                                          <span style={{ color: '#555' }}>Drop-Off Expiry:</span>{' '}
                                          {idx === displayRecords.length - 1 && (job?.containers || []).filter(c => c.container_no).length > 1 ? (
                                            <strong>
                                              <span
                                                title={getDropoffExpiryContainerTitle(job.containers)}
                                                onClick={() => canDelete && openDropoffExpiryModal('direct')}
                                                style={canDelete ? { color: '#1a5fb4', textDecoration: 'underline', cursor: 'pointer' } : {}}
                                              >
                                                {fmtDateShort(rec.drop_off_expiry_date) || '—'}
                                              </span>
                                              {hasMultipleDropoffExpiries(job.containers) && <span style={{ color: 'red', marginLeft: 6 }}>(Multiple)</span>}
                                            </strong>
                                          ) : (
                                            <strong>
                                              {fmtDateShort(rec.drop_off_expiry_date) || '—'}
                                              {hasMultipleDropoffExpiries(job.containers) && <span style={{ color: 'red', marginLeft: 6 }}>(Multiple)</span>}
                                            </strong>
                                          )}
                                        </div>
                                        {rec.drop_off_location && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#555' }}>Location:</span> <strong>{rec.drop_off_location}</strong></div>}
                                      </>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                    {rec.do_attachment_url && (
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.do_attachment_url, 'DO Attachment')}>View DO Attachment</button>
                                    )}
                                    {rec.drop_off_attachment_url && (
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.drop_off_attachment_url, 'Drop-Off Attachment')}>View Drop-Off Attachment</button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* ── First-time entry OR Add New Entry ── */}
                  {(() => {
                    const job = dialog.job;
                    const existingRecords = Array.isArray(job?.do_records) ? job.do_records : [];
                    const hasHistory = existingRecords.length > 0 || !!(dialog.origDoDate);
                    const setNewDo = updater => setDialog(d => ({
                      ...d,
                      newDoEntry: typeof updater === 'function' ? updater(d.newDoEntry) : updater
                    }));
                    const newDo = dialog.newDoEntry || {};

                    const doDateVal = newDo.isOpen ? (newDo.doReceivedDate || '') : dialog.doReceivedDate;
                    const setDoDate = newDo.isOpen
                      ? (v => setNewDo(n => ({ ...n, doReceivedDate: v })))
                      : (v => setDialog(d => ({ ...d, doReceivedDate: v })));

                    const doExpVal = newDo.isOpen ? (newDo.doExpiryDate || '') : dialog.doExpiryDate;
                    const setDoExp = newDo.isOpen
                      ? (v => setNewDo(n => ({ ...n, doExpiryDate: v })))
                      : (v => setDialog(d => ({ ...d, doExpiryDate: v })));

                    const dropDateVal = newDo.isOpen ? (newDo.dropOffDate || '') : dialog.dropOffDate;
                    const setDropDate = newDo.isOpen
                      ? (v => setNewDo(n => ({ ...n, dropOffDate: v })))
                      : (v => setDialog(d => ({ ...d, dropOffDate: v })));

                    const dropExpVal = newDo.isOpen ? (newDo.dropOffExpiryDate || '') : dialog.dropOffExpiryDate;
                    const setDropExp = newDo.isOpen
                      ? (v => setNewDo(n => ({ ...n, dropOffExpiryDate: v })))
                      : (v => setDialog(d => ({ ...d, dropOffExpiryDate: v })));

                    const dropLocVal = newDo.isOpen ? (newDo.dropOffLocation || '') : dialog.dropOffLocation;
                    const setDropLoc = newDo.isOpen
                      ? (v => setNewDo(n => ({ ...n, dropOffLocation: v })))
                      : (v => setDialog(d => ({ ...d, dropOffLocation: v })));

                    const setDoFile = newDo.isOpen
                      ? (f => setNewDo(n => {
                          if (n.doAttachmentPreviewUrl) URL.revokeObjectURL(n.doAttachmentPreviewUrl);
                          return { ...n, doAttachmentFile: f, doAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                        }))
                      : (f => setDialog(d => {
                          if (d.doAttachmentPreviewUrl) URL.revokeObjectURL(d.doAttachmentPreviewUrl);
                          return { ...d, doAttachmentFile: f, doAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                        }));

                    const setDropFile = newDo.isOpen
                      ? (f => setNewDo(n => {
                          if (n.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(n.dropoffAttachmentPreviewUrl);
                          return { ...n, dropOffAttachmentFile: f, dropoffAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                        }))
                      : (f => setDialog(d => {
                          if (d.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(d.dropoffAttachmentPreviewUrl);
                          return { ...d, dropOffAttachmentFile: f, dropoffAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                        }));

                    const doPreviewUrl = newDo.isOpen ? (newDo.doAttachmentPreviewUrl || '') : (dialog.doAttachmentPreviewUrl || '');
                    const dropPreviewUrl = newDo.isOpen ? (newDo.dropoffAttachmentPreviewUrl || '') : (dialog.dropoffAttachmentPreviewUrl || '');

                    const { origDoDate: _origDoDate, origDoExpiry: _origDoExpiry, origDropDate: _origDropDate, origDropExpiry: _origDropExpiry, origDropLocation: _origDropLocation } = dialog;
                    const prevRecord = existingRecords.length > 0
                      ? existingRecords[existingRecords.length - 1]
                      : (_origDoDate ? {
                          do_received_date: _origDoDate,
                          do_expiry_date: _origDoExpiry,
                          drop_off_date: _origDropDate,
                          drop_off_expiry_date: _origDropExpiry,
                          drop_off_location: _origDropLocation,
                          do_attachment_url: (() => { const l = findLetterByType(job, 'DO ATTACHMENT'); return l ? (l.generated_file || l.file || l.document || l.url || l.file_url || null) : null; })(),
                          drop_off_attachment_url: (() => { const l = findLetterByType(job, 'DROPOFF ATTACHMENT'); return l ? (l.generated_file || l.file || l.document || l.url || l.file_url || null) : null; })(),
                        } : null);

                    const usePrevDO = newDo.usePreviousDO;
                    const usePrevDrop = newDo.usePreviousDropoff;

                    const displayDoDate = usePrevDO ? (prevRecord?.do_received_date || '') : doDateVal;
                    const displayDoExp = usePrevDO ? (prevRecord?.do_expiry_date || '') : doExpVal;
                    const displayDropDate = usePrevDrop ? (prevRecord?.drop_off_date || '') : dropDateVal;
                    const displayDropExp = usePrevDrop ? (prevRecord?.drop_off_expiry_date || '') : dropExpVal;
                    const displayDropLoc = usePrevDrop ? (prevRecord?.drop_off_location || '') : dropLocVal;

                    if (!newDo.isOpen) {
                      return (
                        <div style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className="dialog-btn dialog-btn-primary"
                            disabled={!canEdit}
                            onClick={() => setNewDo(n => ({
                              ...n,
                              isOpen: true,
                              doReceivedDate: formatDateForInput(prevRecord?.do_received_date) || n.doReceivedDate || '',
                              doExpiryDate: formatDateForInput(prevRecord?.do_expiry_date) || n.doExpiryDate || '',
                              dropOffDate: formatDateForInput(prevRecord?.drop_off_date) || n.dropOffDate || '',
                              dropOffExpiryDate: formatDateForInput(prevRecord?.drop_off_expiry_date) || n.dropOffExpiryDate || '',
                              dropOffLocation: prevRecord?.drop_off_location || n.dropOffLocation || ''
                            }))}
                          >
                            + Add New DO Entry
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div style={hasHistory ? { borderTop: '2px dashed #c5d8f5', paddingTop: 12, marginTop: 4 } : {}}>
                        {hasHistory && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <span style={{ fontWeight: 'bold', fontSize: 12, color: '#1a5fb4', textTransform: 'uppercase', letterSpacing: 1 }}>+ Add New DO Entry</span>
                            <button
                              type="button"
                              className="dialog-btn dialog-btn-secondary dialog-btn-sm"
                              onClick={() => setNewDo(n => ({ ...n, isOpen: false }))}
                            >
                              Cancel
                            </button>
                          </div>
                        )}

                        {/* ── DO Section ── */}
                        <div style={{ background: usePrevDO ? '#f0f6ff' : 'transparent', border: usePrevDO ? '1px solid #c5d8f5' : 'none', borderRadius: 4, padding: usePrevDO ? '10px 12px' : '0', marginBottom: 12 }}>
                          {hasHistory && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={!!usePrevDO}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setNewDo(n => ({
                                    ...n,
                                    usePreviousDO: checked,
                                    doReceivedDate: !checked ? formatDateForInput(prevRecord?.do_received_date) : '',
                                    doExpiryDate: !checked ? formatDateForInput(prevRecord?.do_expiry_date) : '',
                                    doAttachmentFile: null,
                                    doAttachmentPreviewUrl: ''
                                  }));
                                }}
                                disabled={!canEdit}
                              />
                              <span style={{ fontWeight: 'bold', color: '#1a5fb4' }}>Use Previous DO Details</span>
                              {usePrevDO && prevRecord?.do_received_date && (
                                <span style={{ fontSize: 11, color: '#555', fontWeight: 'normal' }}>({fmtDateShort(prevRecord.do_received_date)})</span>
                              )}
                            </label>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Received Date {!usePrevDO && <span style={{ color: 'red' }}>*</span>}</label>
                              <input type="date" className="search-input" value={displayDoDate} max={new Date().toISOString().split('T')[0]}
                                onChange={e => !usePrevDO && setDoDate(e.target.value)}
                                readOnly={usePrevDO || !canEdit}
                                style={usePrevDO ? { background: '#e8f0fe', color: '#555' } : {}}
                              />
                            </div>
                            <div>
                              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Expiry Date</label>
                              <input type="date" className="search-input" value={displayDoExp}
                                onChange={e => !usePrevDO && setDoExp(e.target.value)}
                                readOnly={usePrevDO || !canEdit}
                                style={usePrevDO ? { background: '#e8f0fe', color: '#555' } : {}}
                                min={displayDoDate || undefined}
                              />
                            </div>
                          </div>
                          <div style={{ marginTop: 12 }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Attachment</label>
                            {usePrevDO ? (
                              prevRecord?.do_attachment_url ? (
                                <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(prevRecord.do_attachment_url, 'DO Attachment (Previous)')}>
                                  View Previous Attachment
                                </button>
                              ) : <span style={{ fontSize: 12, color: '#888' }}>No previous attachment</span>
                            ) : (
                              <>
                                {!hasHistory && findLetterByType(dialog.job, 'DO ATTACHMENT') && (
                                  <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" style={{ marginBottom: 6 }} onClick={() => {
                                    const letter = findLetterByType(dialog.job, 'DO ATTACHMENT');
                                    const url = letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || '';
                                    if (url) openFilePreview(url, 'DO Attachment');
                                  }}>View Current Attachment</button>
                                )}
                                <input type="file" onChange={e => setDoFile(e.target.files[0])} disabled={!canEdit} />
                                {doPreviewUrl && (
                                  <button
                                    type="button"
                                    className="dialog-btn dialog-btn-primary dialog-btn-sm"
                                    style={{ marginTop: 6 }}
                                    onClick={() => openFilePreview(doPreviewUrl, (hasHistory ? newDo.doAttachmentFile : dialog.doAttachmentFile)?.name || 'DO Attachment (Selected)')}
                                  >
                                    View Selected Attachment
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* ── Drop-Off Section ── */}
                        {showDropoffFields && (
                          <div style={{ background: usePrevDrop ? '#f0f6ff' : 'transparent', border: usePrevDrop ? '1px solid #c5d8f5' : 'none', borderRadius: 4, padding: usePrevDrop ? '10px 12px' : '0' }}>
                            {hasHistory && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
                                <input
                                  type="checkbox"
                                  checked={!!usePrevDrop}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setNewDo(n => ({
                                      ...n,
                                      usePreviousDropoff: checked,
                                      dropOffDate: !checked ? formatDateForInput(prevRecord?.drop_off_date) : '',
                                      dropOffExpiryDate: !checked ? formatDateForInput(prevRecord?.drop_off_expiry_date) : '',
                                      dropOffLocation: !checked ? (prevRecord?.drop_off_location || '') : '',
                                      dropOffAttachmentFile: null,
                                      dropoffAttachmentPreviewUrl: ''
                                    }));
                                  }}
                                  disabled={!canEdit}
                                />
                                <span style={{ fontWeight: 'bold', color: '#1a5fb4' }}>Use Previous Drop-Off Details</span>
                                {usePrevDrop && prevRecord?.drop_off_date && (
                                  <span style={{ fontSize: 11, color: '#555', fontWeight: 'normal' }}>({fmtDateShort(prevRecord.drop_off_date)})</span>
                                )}
                              </label>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                              <div>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop-Off Received Date {!usePrevDrop && <span style={{ color: 'red' }}>*</span>}</label>
                                <input type="date" className="search-input" value={displayDropDate} max={new Date().toISOString().split('T')[0]}
                                  onChange={e => !usePrevDrop && setDropDate(e.target.value)}
                                  readOnly={usePrevDrop || !canEdit}
                                  style={usePrevDrop ? { background: '#e8f0fe', color: '#555' } : {}}
                                />
                              </div>
                              <div>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop-Off Expiry Date {!usePrevDrop && !hasHistory && <span style={{ color: 'red' }}>*</span>}</label>
                                {usePrevDrop ? (
                                  <input type="date" className="search-input" value={displayDropExp} readOnly style={{ background: '#e8f0fe', color: '#555' }} />
                                ) : ((job?.containers || []).filter(c => c.container_no).length > 1) ? (
                                  (hasHistory && !newDo.dropOffExpiryDate) ? (
                                    <a
                                      href="#"
                                      onClick={e => { e.preventDefault(); openDropoffExpiryModal('staged', 'newdo'); }}
                                      style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                    >
                                      Add Drop-Off Expiry
                                    </a>
                                  ) : (
                                    <a
                                      href="#"
                                      onClick={e => { e.preventDefault(); openDropoffExpiryModal('staged', 'newdo'); }}
                                      title={getDropoffExpiryContainerTitle(job.containers)}
                                      style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                    >
                                      {fmtDateShort(hasHistory ? newDo.dropOffExpiryDate : computeEarliestDropoffExpiry(job.containers)) || 'Enter Date'}
                                    </a>
                                  )
                                ) : (
                                  (hasHistory && !newDo.showDropoffExpiry && !newDo.dropOffExpiryDate) ? (
                                    <a
                                      href="#"
                                      onClick={e => { e.preventDefault(); setNewDo(n => ({ ...n, showDropoffExpiry: true })); }}
                                      style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                    >
                                      Add Drop-Off Expiry
                                    </a>
                                  ) : (
                                    <input type="date" className="search-input" value={displayDropExp}
                                      onChange={e => setDropExp(e.target.value)}
                                      readOnly={!canEdit}
                                      min={displayDropDate || undefined}
                                    />
                                  )
                                )}
                              </div>
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop-Off Location</label>
                              {usePrevDrop ? (
                                <input type="text" className="search-input" value={displayDropLoc} readOnly style={{ background: '#e8f0fe', color: '#555' }} />
                              ) : (
                                <Select2Dropdown options={dropOffLocations.map(loc => ({ id: loc.name, text: loc.display_name }))} value={dropLocVal} onChange={val => setDropLoc(val)} valueKey="id" displayKey="text" placeholder="Select or type location" tags={true} disabled={!canEdit} />
                              )}
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop Off Attachment {!usePrevDrop && <span style={{ color: 'red' }}>*</span>}</label>
                              {usePrevDrop ? (
                                prevRecord?.drop_off_attachment_url ? (
                                  <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(prevRecord.drop_off_attachment_url, 'Drop-Off Attachment (Previous)')}>
                                    View Previous Attachment
                                  </button>
                                ) : <span style={{ fontSize: 12, color: '#888' }}>No previous attachment</span>
                              ) : (
                                <>
                                  {!hasHistory && findLetterByType(dialog.job, 'DROPOFF ATTACHMENT') && (
                                    <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" style={{ marginBottom: 6 }} onClick={() => {
                                      const letter = findLetterByType(dialog.job, 'DROPOFF ATTACHMENT');
                                      const url = letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || '';
                                      if (url) openFilePreview(url, 'Drop Off Attachment');
                                    }}>View Current Attachment</button>
                                  )}
                                  <input type="file" onChange={e => setDropFile(e.target.files[0])} disabled={!canEdit} />
                                  {dropPreviewUrl && (
                                    <button
                                      type="button"
                                      className="dialog-btn dialog-btn-primary dialog-btn-sm"
                                      style={{ marginTop: 6 }}
                                      onClick={() => openFilePreview(dropPreviewUrl, (hasHistory ? newDo.dropOffAttachmentFile : dialog.dropOffAttachmentFile)?.name || 'Drop-Off Attachment (Selected)')}
                                    >
                                      View Selected Attachment
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Additional Letter section ── */}
                  {hasDoDetailStatusSelected && (
                    <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
                      {(() => {
                        const selectedType = (dialog.additionalLetterType || '').trim();
                        const additionalLetter = selectedType
                          ? findLetterByType(dialog.job, selectedType)
                          : findLatestAdditionalLetter(dialog.job);
                        const additionalTypeLabel = (additionalLetter?.letter_type || additionalLetter?.type || '').toString().trim();
                        if (!additionalLetter) return null;
                        return (
                          <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <button
                              type="button"
                              className="dialog-btn dialog-btn-primary dialog-btn-sm"
                              onClick={() => {
                                const url = additionalLetter.generated_file || additionalLetter.file || additionalLetter.document || additionalLetter.url || additionalLetter.file_url || '';
                                if (url) openFilePreview(url, additionalTypeLabel || 'Additional Letter');
                              }}
                            >
                              View Additional Letter{additionalTypeLabel ? ` (${additionalTypeLabel})` : ''}
                            </button>
                          </div>
                        );
                      })()}
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Additional Letter Type</label>
                      <input
                        type="text"
                        className="search-input"
                        placeholder="e.g. CLIENT INSTRUCTION"
                        value={dialog.additionalLetterType || ''}
                        onChange={e => setDialog(d => ({ ...d, additionalLetterType: e.target.value }))}
                        readOnly={!canEdit}
                      />
                      <label style={{ fontWeight: 'bold', display: 'block', margin: '10px 0 4px' }}>Additional Letter File</label>
                      <input
                        type="file"
                        onChange={e => setDialog(d => ({ ...d, additionalLetterFile: e.target.files[0] }))}
                        disabled={!canEdit}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="dialog-footer">
            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={closeDialog}>Cancel</button>
            {canEdit && (
              <button type="button" className="dialog-btn dialog-btn-primary" disabled={dialog.saving} onClick={saveShippingDetails}>{dialog.saving ? 'Saving...' : 'Save'}</button>
            )}
          </div>
        </div>
      </div>

      {/* Drop-Off Expiry Date per-container modal */}
      {showDropoffExpiryModal && (
        <div className="modal-overlay" onClick={closeDropoffExpiryModal}>
          <div className="dialog-container" style={{ width: 600, maxHeight: '80vh', overflowY: 'auto', margin: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Drop-Off Expiry Dates (IDF: {dialog.job?.idf_no || 'N/A'})</h3>
            </div>
            <div className="dialog-body">
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', background: '#f4f4f4', padding: '10px', borderRadius: '4px' }}>
                <label style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Bulk Drop-Off Expiry</label>
                <input type="date" className="search-input" value={bulkDropoffExpiryDate} onChange={e => setBulkDropoffExpiryDate(e.target.value)} readOnly={!canEditDropoffExpiryModal} style={{ width: 150 }} />
                <button className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={applyBulkDropoffExpiry} disabled={!canEditDropoffExpiryModal || !bulkDropoffExpiryDate || selectedDropoffContainerIds.length === 0}>Apply to Selected</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="jobs-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={selectedDropoffContainerIds.length === dropoffExpiryContainers.length && dropoffExpiryContainers.length > 0} disabled={!canEditDropoffExpiryModal} onChange={e => { if (e.target.checked) setSelectedDropoffContainerIds(dropoffExpiryContainers.map(c => c.id)); else setSelectedDropoffContainerIds([]); }} /></th>
                      <th>Container No</th>
                      <th>Drop-Off Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dropoffExpiryContainers.map(c => (
                      <tr key={c.id}>
                        <td><input type="checkbox" checked={selectedDropoffContainerIds.includes(c.id)} disabled={!canEditDropoffExpiryModal} onChange={() => handleDropoffExpirySelectionChange(c.id)} /></td>
                        <td>{c.container_no}</td>
                        <td><input type="date" className="search-input" value={c.drop_off_expiry_date || ''} onChange={e => handleDropoffExpiryContainerChange(c.id, e.target.value)} readOnly={!canEditDropoffExpiryModal} style={{ width: 150 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-secondary" onClick={closeDropoffExpiryModal}>Cancel</button>
              {canEditDropoffExpiryModal && <button className="dialog-btn dialog-btn-primary" onClick={saveDropoffExpiryChanges} disabled={savingDropoffExpiry}>{savingDropoffExpiry ? 'Saving...' : 'Save'}</button>}
            </div>
          </div>
        </div>
      )}

      {/* Delete DO record confirmation modal */}
      {deletingDoRecord && (
        <div className="modal-overlay" onClick={closeDeleteDoRecordModal}>
          <div className="dialog-container" style={{ width: 400, margin: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="dialog-body">
              <p>Are you sure you want to delete this DO history record?</p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-secondary" onClick={closeDeleteDoRecordModal} disabled={doRecordSaving}>Cancel</button>
              <button className="dialog-btn dialog-btn-sm" onClick={doDeleteDoRecord} disabled={doRecordSaving} style={{ background: '#c62828', color: '#fff', borderColor: '#c62828' }}>{doRecordSaving ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Free Days prompt */}
      {showFreeDaysConfirm && (
        <div className="modal-overlay" onClick={() => setShowFreeDaysConfirm(false)}>
          <div className="dialog-container" style={{ width: 400, margin: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Confirm Free Days</h3>
            </div>
            <div className="dialog-body">
              <p>Have you confirmed Free Days with BL?</p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-secondary" onClick={() => setShowFreeDaysConfirm(false)}>Cancel</button>
              <button className="dialog-btn dialog-btn-primary" onClick={() => {
                setDialog(d => (d ? { ...d, tempConfirmFreeDays: true } : d));
                setShowFreeDaysConfirm(false);
              }}>Yes</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShippingDetailsDialog;
